import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import InquiryForm from '../components/InquiryForm';
import ImageGallery from '../components/ImageGallery';
import InteractiveMap from '../components/InteractiveMap';
import { useTranslation } from '../contexts/LanguageContext';
import { useMarket } from '../contexts/MarketContext';
import { sanitizeHtml } from '../lib/sanitize';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { MapPin, Square, BedDouble, Euro, Phone, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListingDetailPage() {
  const { t } = useTranslation();
  const { market } = useMarket();
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/listings/${listingId}`, { params: { market } })
      .then(r => {
        // Sold/rented/draft listings are not publicly visible, even via direct link
        if (['sold', 'rented', 'draft'].includes(r.data?.status)) {
          setError(true);
        } else {
          setListing(r.data);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [listingId, market]);

  const LISTING_TYPES_MAP = { sale: t('listings.sale'), rent: t('listings.rent') };
  const CATEGORIES_MAP = {
    apartment: t('cat.apartment'), house: t('cat.house'), villa: t('cat.villa'),
    land: t('cat.land'), office: t('cat.office'), commercial: t('cat.commercial'),
    garage: t('cat.garage'), studio: t('cat.studio'), project: t('cat.project')
  };

  if (loading) return (
    <div className="min-h-screen bg-background"><Header /><div className="flex items-center justify-center min-h-[60vh]"><div className="spinner"></div></div><Footer /></div>
  );

  if (error || !listing) return (
    <div className="min-h-screen bg-background"><Header />
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-lg text-muted-foreground">{t('listings.not_found')}</p>
        <Link to="/listings"><Button variant="outline"><ArrowLeft className="w-4 h-4 mr-2" />{t('listings.back')}</Button></Link>
      </div>
    <Footer /></div>
  );

  const formatPrice = (price, type) => {
    const formatted = new Intl.NumberFormat('bg-BG').format(price);
    return type === 'rent' ? `${formatted} EUR${t('listings.per_month')}` : `${formatted} EUR`;
  };

  // Localized description with fallback to Bulgarian if this market's translation is missing
  const DESC_FIELD_BY_LANG = { bg: 'description', el: 'description_el', tr: 'description_tr', en: 'description_en' };
  const localizedDescription = (() => {
    const field = DESC_FIELD_BY_LANG[market] || 'description';
    const text = listing[field];
    return (text && text.trim()) ? text : listing.description;
  })();

  return (
    <div className="min-h-screen bg-background" data-testid="listing-detail-page">
      <Header />
      <section className="pt-20">
        <div className="relative h-[50vh] min-h-[400px]">
          <img src={listing.images?.[0] || '/og-image.jpg'} alt={listing.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="max-w-7xl mx-auto">
              <Link to="/listings" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors">
                <ArrowLeft className="w-4 h-4 mr-2" />{t('listings.back')}
              </Link>
              <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">{listing.title}</h1>
              <div className="flex flex-wrap items-center gap-3">
                <Badge className={listing.listing_type === 'sale' ? 'bg-[#1B5E37] text-white' : 'bg-blue-600 text-white'}>
                  {LISTING_TYPES_MAP[listing.listing_type]}
                </Badge>
                <Badge variant="secondary" className="bg-white/90 text-foreground">
                  {CATEGORIES_MAP[listing.category] || listing.category}
                </Badge>
                <span className="text-white/80 flex items-center gap-1"><MapPin className="w-4 h-4" />{listing.location}{listing.neighborhood ? `, кв. ${listing.neighborhood}` : ''}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-10">
              <div className="flex flex-wrap gap-6 p-6 bg-white border border-border rounded-xl shadow-sm">
                <div>
                  <span className="text-sm text-muted-foreground">{t('listings.price')}</span>
                  <p className="text-3xl font-bold text-[#1B5E37]">{formatPrice(listing.price, listing.listing_type)}</p>
                </div>
                {listing.area && (<div><span className="text-sm text-muted-foreground">{t('listings.area')}</span><p className="text-xl font-semibold flex items-center gap-1"><Square className="w-5 h-5" />{listing.area} {t('property.area')}</p></div>)}
                {listing.rooms && (<div><span className="text-sm text-muted-foreground">{t('listings.rooms_label')}</span><p className="text-xl font-semibold flex items-center gap-1"><BedDouble className="w-5 h-5" />{listing.rooms}</p></div>)}
                {listing.floor && (<div><span className="text-sm text-muted-foreground">{t('listings.floor_label')}</span><p className="text-xl font-semibold">{listing.floor}</p></div>)}
                {listing.year_built && (<div><span className="text-sm text-muted-foreground">{t('listings.year')}</span><p className="text-xl font-semibold">{listing.year_built}</p></div>)}
              </div>

              <div>
                <h2 className="font-heading text-2xl font-semibold mb-4">{t('listings.description')}</h2>
                <div className="text-muted-foreground leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(localizedDescription || '') }} />
              </div>

              {listing.features?.length > 0 && (
                <div>
                  <h2 className="font-heading text-2xl font-semibold mb-4">{t('property.features')}</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {listing.features.map((f, i) => (
                      <div key={i} className="flex items-center gap-2 p-3 bg-secondary rounded-lg text-sm"><div className="w-2 h-2 bg-[#1B5E37] rounded-full" />{f}</div>
                    ))}
                  </div>
                </div>
              )}

              {listing.images?.length > 1 && (
                <div>
                  <h2 className="font-heading text-2xl font-semibold mb-4">{t('listings.gallery')}</h2>
                  <ImageGallery images={listing.images} title={listing.title} />
                </div>
              )}

              {listing.latitude && listing.longitude && (
                <div>
                  <h2 className="font-heading text-2xl font-semibold mb-4">{t('listings.location')}</h2>
                  <div className="w-full h-[350px] rounded-xl overflow-hidden border border-border" data-testid="listing-map">
                    <InteractiveMap center={{ lat: listing.latitude, lng: listing.longitude }} zoom={15} singleMarker={true} />
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-28 space-y-6">
                {listing.contact_phone && (
                  <div className="p-6 bg-[#1B5E37]/5 border border-[#1B5E37]/20 rounded-xl">
                    <h3 className="font-heading font-semibold text-lg mb-4">{t('listings.contact')}</h3>
                    {listing.contact_name && <p className="font-medium mb-3">{listing.contact_name}</p>}
                    <a href={`tel:${listing.contact_phone.replace(/\s/g, '')}`} className="flex items-center gap-2 text-[#1B5E37] font-semibold text-lg mb-3 hover:underline">
                      <Phone className="w-5 h-5" />{listing.contact_phone}
                    </a>
                    <Button className="w-full" asChild>
                      <a href={`tel:${listing.contact_phone.replace(/\s/g, '')}`}>{t('listings.call')}</a>
                    </Button>
                  </div>
                )}
                <div className="p-6 bg-white border border-border rounded-xl shadow-sm">
                  <h3 className="font-heading font-semibold text-lg mb-4">{t('property.send_inquiry')}</h3>
                  <InquiryForm propertyName={listing.title} listingId={listing.id} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
